/**
 * Create/run tests: AWS threat tests, check_test, abort_test, cleanup_test.
 * AWS threats use async batch (ECS/Docker) + polling so API Gateway / ALB timeouts do not apply.
 */

import { ARBITIUM_EXTENSION, callHandler } from '../core';
import type { HandlerCallResponse } from '../core';
import { getAuthHeaders, getPayloadBase, requireConfig } from '../core/request';

export type AwsTestSubhandler =
  | 'public_s3_bucket'
  | 'iam_no_mfa'
  | 'stale_access_keys'
  | 'open_ssh_sg'
  | 'root_activity'
  | 'privilege_escalation';

export type AwsOktaSubhandler =
  | 'brute_force_password_spray'
  | 'admin_role_escalation'
  | 'impossible_travel'
  | 'mfa_reset_suspicious_login'
  | 'dormant_account_reactivation';

const EXTENSION = ARBITIUM_EXTENSION;
const AWS_THREATS_HANDLER = 'aws_threats';
const OKTA_THREATS_HANDLER = 'okta_threats';

/** Batch poll interval (aligned with lab batch UI). */
const BATCH_POLL_MS = 2500;
/** Max wait: CloudTrail sleep + IAM/S3 work (well under 20 min). */
const BATCH_MAX_WAIT_MS = 20 * 60 * 1000;

async function runThreatBatch(
  subhandler: string,
  threatHandlerSlug: string,
  displayName: string
): Promise<HandlerCallResponse> {
  const { apiBase, portfolio, org } = requireConfig();
  const base = `${apiBase}/_schd/${portfolio}/${org}`;
  const headers = await getAuthHeaders();
  const body = {
    ...getPayloadBase(),
    subhandler,
    //scan: 'auto', // delete for manual scan mode
  };

  const startRes = await fetch(`${base}/call/${EXTENSION}/${threatHandlerSlug}/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const startData = (await startRes.json().catch(() => ({}))) as {
    success?: boolean;
    request_id?: string;
    error?: string;
  };

  if (!startRes.ok || !startData.success || !startData.request_id) {
    const err =
      startData.error ||
      (startData as { message?: string }).message ||
      `Start failed (HTTP ${startRes.status}). Is ${threatHandlerSlug} on EXTERNAL_HANDLERS_ECS_HANDLERS and ECS configured?`;
    return {
      success: false,
      error: err,
      output: [{ error: err }] as unknown as HandlerCallResponse['output'],
    };
  }

  const requestId = startData.request_id;
  const resultUrl = `${base}/batch/result?extension=${encodeURIComponent(EXTENSION)}&request_id=${encodeURIComponent(requestId)}`;
  const authOnly = { Authorization: headers.Authorization! };
  const deadline = Date.now() + BATCH_MAX_WAIT_MS;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, BATCH_POLL_MS));
    const r = await fetch(resultUrl, { headers: authOnly });
    const d = (await r.json().catch(() => ({}))) as {
      status?: string;
      success?: boolean;
      output?: unknown;
      error?: string;
    };

    if (d.status === 'pending') continue;

    if (d.status === 'completed') {
      if (d.success && d.output != null) {
        const out = d.output as Record<string, unknown>;
        if (typeof out.success === 'boolean' && Array.isArray(out.output)) {
          return out as HandlerCallResponse;
        }
        if (typeof out.success === 'boolean') {
          return out as HandlerCallResponse;
        }
        return { success: true, output: out as HandlerCallResponse['output'] };
      }
      const msg = d.error || `${displayName} threat handler failed`;
      return {
        success: false,
        error: msg,
        output: [{ error: msg }] as unknown as HandlerCallResponse['output'],
      };
    }

    if (d.status === 'error') {
      return {
        success: false,
        error: d.error || 'Batch result error',
        output: [{ error: d.error }] as unknown as HandlerCallResponse['output'],
      };
    }
  }

  const msg = `Timed out waiting for ${displayName} threat test (batch). Check Monitor tests — the job may still complete.`;
  return { success: false, error: msg, output: [{ error: msg }] as unknown as HandlerCallResponse['output'] };
}

async function startThreatBatch(
  subhandler: string,
  threatHandlerSlug: string,
  displayName: string
): Promise<HandlerCallResponse> {
  const { apiBase, portfolio, org } = requireConfig();
  const base = `${apiBase}/_schd/${portfolio}/${org}`;
  const headers = await getAuthHeaders();
  const body = {
    ...getPayloadBase(),
    subhandler,
  };

  const startRes = await fetch(`${base}/call/${EXTENSION}/${threatHandlerSlug}/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const startData = (await startRes.json().catch(() => ({}))) as {
    success?: boolean;
    request_id?: string;
    error?: string;
    message?: string;
  };

  if (!startRes.ok || !startData.success || !startData.request_id) {
    const err =
      startData.error ||
      startData.message ||
      `Start failed (HTTP ${startRes.status}). Is ${threatHandlerSlug} on EXTERNAL_HANDLERS_ECS_HANDLERS and ECS configured?`;
    return {
      success: false,
      error: err,
      output: [{ error: err }] as unknown as HandlerCallResponse['output'],
    };
  }

  return {
    success: true,
    output: {
      request_id: startData.request_id,
      status: 'pending',
      message: `${displayName} threat test started`,
    } as unknown as HandlerCallResponse['output'],
  };
}

/**
 * Runs an AWS test threat via async batch: POST .../call/arbitium/aws_threats/start
 * with subhandler in body, then poll batch/result until ECS/Docker finishes.
 */
export async function runAwsThreat(subhandler: AwsTestSubhandler): Promise<HandlerCallResponse> {
  return runThreatBatch(subhandler, AWS_THREATS_HANDLER, 'AWS');
}

export async function runOktaThreat(subhandler: AwsOktaSubhandler): Promise<HandlerCallResponse> {
  return runThreatBatch(subhandler, OKTA_THREATS_HANDLER, 'Okta');
}

/**
 * Starts an AWS test threat via async batch and returns immediately with request_id.
 */
export async function startAwsThreat(subhandler: AwsTestSubhandler): Promise<HandlerCallResponse> {
  return startThreatBatch(subhandler, AWS_THREATS_HANDLER, 'AWS');
}

/**
 * Starts an Okta test threat via async batch and returns immediately with request_id.
 */
export async function startOktaThreat(subhandler: AwsOktaSubhandler): Promise<HandlerCallResponse> {
  return startThreatBatch(subhandler, OKTA_THREATS_HANDLER, 'Okta');
}
/**
 * Runs check_test. Pass test_id to check a single test; omit to process all in_progress.
 */
export async function checkTest(testId?: string): Promise<HandlerCallResponse> {
  const payload: Record<string, unknown> = {};
  if (testId) payload.test_id = testId;
  return callHandler(EXTENSION, 'check_test', payload);
}

/**
 * Aborts a test (check_test with abort: true).
 */
export async function abortTest(testId: string): Promise<HandlerCallResponse> {
  return callHandler(EXTENSION, 'check_test', { test_id: testId, abort: true });
}

/**
 * Runs cleanup_test. Pass test_id for a single test; omit for all pending.
 */
export async function cleanupTest(testId?: string): Promise<HandlerCallResponse> {
  const payload: Record<string, unknown> = {};
  if (testId) payload.test_id = testId;
  return callHandler(EXTENSION, 'cleanup_test', payload);
}
